import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import {
    LuCalendarDays,
    LuCheck,
    LuChevronDown,
    LuChevronUp,
    LuPencil,
    LuPlus,
    LuTrash2,
    LuX,
} from "react-icons/lu";
import FloatingField from "./FloatingField";
import { phaseImage } from "../constants";

// Only these phases carry a "number of revisions" (Vision has none).
const REVISION_PHASES = ["concept", "render", "material", "drawing"];
export const allowsRevisions = (name = "") => {
    const n = String(name).toLowerCase();
    return REVISION_PHASES.some((p) => n.includes(p));
};

// Positive-integer-only handler (no leading zero) for the week / revision
// text inputs — text fields so there are no spinner arrows or scroll edits.
const handlePositiveInt = (raw, apply) => {
    if (raw === "" || /^[1-9]\d*$/.test(raw)) apply(raw);
};

// One sub-deliverable line — display mode with edit/delete actions, or an
// inline input with save/cancel. Mirrors reno-portal's DeliverableRow.
function SubDeliverableRow({ value, index, disabled, onSave, onDelete }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);

    if (editing) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    position: "relative",
                    marginBottom: "8px",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        left: "12px",
                        color: "#4D6BF6",
                        display: "flex",
                        pointerEvents: "none",
                        zIndex: 1,
                    }}
                >
                    <LuPlus size={16} />
                </Box>
                <input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Deliverable name…"
                    style={{
                        flex: 1,
                        minWidth: 0,
                        borderRadius: "8px",
                        border: "1px solid #d1d5db",
                        padding: "9px 12px 9px 36px",
                        fontSize: "14px",
                        color: "#0d0d0e",
                        fontFamily: "Inter Tight",
                        outline: "none",
                    }}
                />
                <button
                    type="button"
                    onClick={() => {
                        if (!draft.trim()) return;
                        onSave(draft.trim());
                        setEditing(false);
                    }}
                    style={{
                        padding: "6px",
                        border: "none",
                        background: "none",
                        color: "#16A34A",
                        cursor: "pointer",
                        display: "flex",
                    }}
                >
                    <LuCheck size={18} />
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setDraft(value);
                        setEditing(false);
                        // A brand-new empty row cancels into deletion.
                        if (!value) onDelete();
                    }}
                    style={{
                        padding: "6px",
                        border: "none",
                        background: "none",
                        color: "#949daa",
                        cursor: "pointer",
                        display: "flex",
                    }}
                >
                    <LuX size={18} />
                </button>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                backgroundColor: "#fff",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "6px 8px",
            }}
        >
            <Typography
                sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#949daa",
                    flexShrink: 0,
                    marginLeft: "4px",
                    fontFamily: "Inter Tight",
                }}
            >
                {index + 1}.
            </Typography>
            <Typography
                sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#0d0d0e",
                    flex: 1,
                    minWidth: 0,
                    marginLeft: "4px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontFamily: "Inter Tight",
                }}
            >
                {value || "Untitled"}
            </Typography>
            {!disabled && (
                <>
                    <button
                        type="button"
                        onClick={() => {
                            setDraft(value);
                            setEditing(true);
                        }}
                        style={{
                            padding: "6px",
                            border: "none",
                            background: "none",
                            color: "#949daa",
                            cursor: "pointer",
                            display: "flex",
                        }}
                    >
                        <LuPencil size={15} />
                    </button>
                    <button
                        type="button"
                        onClick={onDelete}
                        style={{
                            padding: "6px",
                            border: "none",
                            background: "none",
                            color: "#949daa",
                            cursor: "pointer",
                            display: "flex",
                        }}
                    >
                        <LuTrash2 size={16} />
                    </button>
                </>
            )}
        </Box>
    );
}

// One deliverable card — phase illustration, editable name, weeks pill,
// dates, description and its sub-deliverables list.
function DeliverableCard({ deliverable, disabled, onChange, onRemove }) {
    const [expanded, setExpanded] = useState(true);
    const [editingName, setEditingName] = useState(
        !deliverable.id && !deliverable.name,
    );
    const [nameDraft, setNameDraft] = useState(deliverable.name || "");

    // Weeks are now entered directly instead of derived from dates.
    const weeks =
        deliverable.weeks !== "" && deliverable.weeks != null
            ? Number(deliverable.weeks)
            : null;

    const update = (patch) => onChange({ ...deliverable, ...patch });

    const updateSub = (index, value) => {
        const next = [...(deliverable.sub_deliverables || [])];
        next[index] = value;
        update({ sub_deliverables: next });
    };

    const deleteSub = (index) => {
        update({
            sub_deliverables: (deliverable.sub_deliverables || []).filter(
                (_, i) => i !== index,
            ),
        });
    };

    const addSub = () => {
        update({
            sub_deliverables: [...(deliverable.sub_deliverables || []), ""],
        });
    };

    return (
        <Box
            sx={{
                borderRadius: "12px",
                backgroundColor: "#F8F9FB",
                marginBottom: "12px",
                padding: "12px 14px",
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                }}
            >
                <img
                    src={phaseImage(deliverable.name)}
                    alt={deliverable.name || "Deliverable"}
                    style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "8px",
                        objectFit: "cover",
                        flexShrink: 0,
                    }}
                />
                {editingName && !disabled ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        <input
                            autoFocus
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            placeholder="Deliverable name…"
                            style={{
                                flex: 1,
                                minWidth: 0,
                                borderRadius: "8px",
                                border: "1px solid #d1d5db",
                                padding: "8px 12px",
                                fontSize: "14px",
                                color: "#0d0d0e",
                                fontFamily: "Inter Tight",
                                outline: "none",
                            }}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                if (!nameDraft.trim()) return;
                                update({ name: nameDraft.trim() });
                                setEditingName(false);
                            }}
                            style={{
                                padding: "6px",
                                border: "none",
                                background: "none",
                                color: "#16A34A",
                                cursor: "pointer",
                                display: "flex",
                            }}
                        >
                            <LuCheck size={18} />
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setNameDraft(deliverable.name || "");
                                setEditingName(false);
                                if (!deliverable.name && !deliverable.id) {
                                    onRemove();
                                }
                            }}
                            style={{
                                padding: "6px",
                                border: "none",
                                background: "none",
                                color: "#949daa",
                                cursor: "pointer",
                                display: "flex",
                            }}
                        >
                            <LuX size={18} />
                        </button>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#0d0d0e",
                                fontFamily: "Inter Tight",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {deliverable.name || "Untitled"}
                        </Typography>
                        {/* {!disabled && (
                            <button
                                type="button"
                                onClick={() => {
                                    setNameDraft(deliverable.name || "");
                                    setEditingName(true);
                                }}
                                style={{
                                    padding: "4px",
                                    border: "none",
                                    background: "none",
                                    color: "#949daa",
                                    cursor: "pointer",
                                    display: "flex",
                                    flexShrink: 0,
                                }}
                            >
                                <LuPencil size={14} />
                            </button>
                        )} */}
                    </Box>
                )}

                {weeks != null && (
                    <Box
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "4px 12px",
                            borderRadius: "999px",
                            backgroundColor: "#EFF2FE",
                            color: "#4D6BF6",
                            fontSize: "12.5px",
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            fontFamily: "Inter Tight",
                            flexShrink: 0,
                        }}
                    >
                        {weeks} week{weeks === 1 ? "" : "s"}
                    </Box>
                )}

                {!disabled && !deliverable.id && (
                    <button
                        type="button"
                        onClick={onRemove}
                        title="Remove deliverable"
                        style={{
                            padding: "4px",
                            border: "none",
                            background: "none",
                            color: "#949daa",
                            cursor: "pointer",
                            display: "flex",
                            flexShrink: 0,
                        }}
                    >
                        <LuTrash2 size={16} />
                    </button>
                )}

                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    style={{
                        padding: "4px",
                        border: "none",
                        background: "none",
                        color: "#949daa",
                        cursor: "pointer",
                        display: "flex",
                        flexShrink: 0,
                    }}
                >
                    {expanded ? (
                        <LuChevronUp size={18} />
                    ) : (
                        <LuChevronDown size={18} />
                    )}
                </button>
            </Box>

            {expanded && (
                <Box sx={{ marginTop: "14px", paddingLeft: { md: "46px" } }}>
                    {/* Duration (weeks) + Number of revisions (revisions
                        only for concept/render/material/drawing phases) */}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "1fr",
                                sm: "1fr 1fr",
                            },
                            gap: "12px",
                            marginBottom: "14px",
                        }}
                    >
                        <FloatingField
                            label="Duration (weeks)"
                            type="text"
                            placeholder="e.g., 2"
                            inputProps={{
                                inputMode: "numeric",
                                onWheel: (e) => e.target.blur(),
                            }}
                            value={deliverable.weeks ?? ""}
                            onChange={(e) =>
                                handlePositiveInt(e.target.value, (v) =>
                                    update({ weeks: v }),
                                )
                            }
                            disabled={disabled}
                        />
                        {allowsRevisions(deliverable.name) && (
                            <FloatingField
                                label="Number of revisions"
                                type="text"
                                placeholder="e.g., 3"
                                inputProps={{
                                    inputMode: "numeric",
                                    onWheel: (e) => e.target.blur(),
                                }}
                                value={deliverable.revisions ?? ""}
                                onChange={(e) =>
                                    handlePositiveInt(e.target.value, (v) =>
                                        update({ revisions: v }),
                                    )
                                }
                                disabled={disabled}
                            />
                        )}
                    </Box>

                    {/* Description */}
                    <Box sx={{ marginBottom: "14px" }}>
                        <FloatingField
                            label="Description"
                            multiline
                            rows={2}
                            placeholder="Describe this deliverable..."
                            value={deliverable.description || ""}
                            onChange={(e) =>
                                update({ description: e.target.value })
                            }
                            disabled={disabled}
                        />
                    </Box>

                    {/* Sub-deliverables */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                color: "#0d0d0e",
                                fontFamily: "Inter Tight",
                            }}
                        >
                            Deliverables
                        </Typography>
                        {!disabled && (
                            <button
                                type="button"
                                onClick={addSub}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    borderRadius: "999px",
                                    border: "1px dashed #4D6BF6",
                                    color: "#4D6BF6",
                                    background: "none",
                                    padding: "4px 12px",
                                    fontSize: "12.5px",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    fontFamily: "Inter Tight",
                                }}
                            >
                                <LuPlus size={14} />
                                Add deliverable
                            </button>
                        )}
                    </Box>
                    {(deliverable.sub_deliverables || []).length === 0 && (
                        <Typography
                            sx={{
                                fontSize: "12.5px",
                                color: "#949daa",
                                fontFamily: "Inter Tight",
                                marginBottom: "8px",
                            }}
                        >
                            No deliverables added yet.
                        </Typography>
                    )}
                    {(deliverable.sub_deliverables || []).map((sub, i) => (
                        <SubDeliverableRow
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${deliverable.id || "new"}-${i}`}
                            value={sub}
                            index={i}
                            disabled={disabled}
                            onSave={(value) => updateSub(i, value)}
                            onDelete={() => deleteSub(i)}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

// Tab 02 — deliverables editor, same look as the admin portal's create
// design scope Deliverables tab plus start/end dates and a weeks pill.
export default function DesignDeliverables({
    deliverables = [],
    setDeliverables,
    isDisabled = false,
}) {
    const updateAt = (index, next) => {
        setDeliverables((prev) =>
            prev.map((d, i) => (i === index ? next : d)),
        );
    };

    const removeAt = (index) => {
        setDeliverables((prev) => prev.filter((_, i) => i !== index));
    };

    const addDeliverable = () => {
        setDeliverables((prev) => [
            ...prev,
            {
                id: null,
                name: "",
                description: "",
                weeks: "",
                revisions: "",
                sub_deliverables: [],
                metadata: {},
            },
        ]);
    };

    return (
        <Box
            sx={{
                borderRadius: "12px",
                border: "1px solid #E5E7EB",
                margin: "1vh 0",
                padding: "16px",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "4px",
                }}
            >
                <Typography
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "16px",
                        fontWeight: 500,
                        color: "#0d0d0e",
                        fontFamily: "Inter Tight",
                    }}
                >
                    <LuCalendarDays size={20} color="#4D6BF6" />
                    Deliverables
                </Typography>
                {!isDisabled && (
                    <button
                        type="button"
                        onClick={addDeliverable}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            borderRadius: "999px",
                            border: "1px dashed #4D6BF6",
                            color: "#4D6BF6",
                            background: "none",
                            padding: "6px 14px",
                            fontSize: "13px",
                            fontWeight: 500,
                            cursor: "pointer",
                            fontFamily: "Inter Tight",
                        }}
                    >
                        <LuPlus size={14} />
                        Add deliverable
                    </button>
                )}
            </Box>
            <Typography
                sx={{
                    fontSize: "12.5px",
                    color: "#949daa",
                    marginBottom: "16px",
                    fontFamily: "Inter Tight",
                }}
            >
                Add deliverables with their dates, description and
                sub-deliverables.
            </Typography>

            {deliverables.length === 0 && (
                <Typography
                    sx={{
                        fontSize: "13px",
                        color: "#949daa",
                        fontFamily: "Inter Tight",
                    }}
                >
                    No deliverables yet.
                </Typography>
            )}

            {deliverables.map((deliverable, index) => (
                <DeliverableCard
                    key={deliverable.id || `new-${index}`}
                    deliverable={deliverable}
                    disabled={isDisabled}
                    onChange={(next) => updateAt(index, next)}
                    onRemove={() => removeAt(index)}
                />
            ))}
        </Box>
    );
}
